import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Dialog, Spinner, useToast } from '@economic/taco';
import { Trans, t } from '@lingui/macro';
import { useCurrentAgreement } from '@economic/react-app';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import RackbeatLogo from './images/rackbeat-logo.svg';
import ErrorDialog from './ErrorDialog';
import RackbeatOffboardGraphic from './images/man-with-box-in-warehause.svg';
import { trackEvent } from './trackEvent';
import { OnboardingStep } from './types';

function RackbeatLagerOffboardingDialog() {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const agreement = useCurrentAgreement();
    const open = location.hash === '#offboardRackbeatLagerNative';

    const [isLoading, setIsLoading] = useState(false);
    const [errorOccurred, setErrorOccurred] = useState(false);

    const handleOffboardingCompleted = async () => {
        trackOffboardingEvent('complete.clicked');

        try {
            setIsLoading(true);

            const offboardingUrl = agreement.features.rackbeatNewOnboardingFlow
                ? 'rackbeat-lager/company/initiate-offboarding'
                : 'rackbeat-lager/company/revoke-grant';

            const response = await internalApiClient.post(offboardingUrl, {});

            if (response.status === 200) {
                navigate(location.pathname);
                toast.success(t`Successfully offboarded Rackbeat Lager`);
            } else {
                setErrorOccurred(true);
                toast.error(t`Unable to offboard Rackbeat Lager`);
            }
        } catch (error) {
            toast.error(t`Unable to offboard Rackbeat Lager`);
            setErrorOccurred(true);
            trackOffboardingEvent('error-occured');
        } finally {
            setIsLoading(false);
        }
    };

    const closeDialog = () => {
        trackOffboardingEvent('cancel.clicked');

        navigate(location.pathname);
    };

    const trackOffboardingEvent = (eventName: string, trackingData?: object) => {
        trackEvent(OnboardingStep.Offboarded, eventName, trackingData, true);
    };

    if (isLoading) {
        return (
            <Dialog open={open} size="xs" closeOnEscape onClose={closeDialog}>
                <Dialog.Content className="flex flex-col items-center" aria-label={t`TOAST_LOADING`}>
                    <Spinner />
                </Dialog.Content>
            </Dialog>
        );
    }
    if (errorOccurred) {
        return (
            <Dialog size="xs" open={open} closeOnEscape onClose={closeDialog}>
                <Dialog.Content aria-label="rackbeatlager">
                    <ErrorDialog
                        message={t`A technical error occurred on our side, so we are unable to offboard your agreement at the moment.`}>
                        <></>
                    </ErrorDialog>
                    <Dialog.Footer className="flex justify-between bg-grey-100 -mx-6 -mb-6 align-middle pt-2 pb-4">
                        <div className="flex text-end ml-6 pt-2">
                            <div className="text-xs mr-4 mt-1">
                                <Trans>In close collaboration with</Trans>
                            </div>
                            <div>
                                <img src={RackbeatLogo} className="h-[24px]" />
                            </div>
                        </div>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog>
        );
    }

    if (!agreement.features.rackbeatNewOnboardingFlow) {
        return (
            <Dialog size="xs" open={open} closeOnEscape onClose={closeDialog}>
                <Dialog.Content aria-label="rackbeatlager">
                    <Dialog.Title>
                        <Trans>Information</Trans>
                    </Dialog.Title>
                    <div className="text-left mt-4 mb-6">
                        <strong>
                            <Trans>Do you want to remove Lager?</Trans>
                        </strong>
                    </div>
                    <div className="text-left mt-4 mb-6">
                        <Trans>If you remove the app, Rackbeat will no longer have access to your agreement.</Trans>
                    </div>
                    <div className="text-left mt-4 mb-6">
                        <Trans>Remember to also deregister in Rackbeat.</Trans>
                    </div>
                    <div className="text-left mt-4 mb-6">
                        <Trans>You can always add Lager- by Rackbeat again.</Trans>
                    </div>
                    <div className="flex justify-end">
                        <div className="space-x-4">
                            <Button appearance="default" onClick={closeDialog}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button appearance="primary" onClick={handleOffboardingCompleted}>
                                <Trans>Ok</Trans>
                            </Button>
                        </div>
                    </div>
                    <Dialog.Footer className="flex justify-between bg-grey-100 -mx-6 -mb-6 align-middle pt-2 pb-4">
                        <div className="flex text-end ml-6 pt-2">
                            <div className="text-xs mr-4 mt-1">
                                <Trans>In close collaboration with</Trans>
                            </div>
                            <div>
                                <img src={RackbeatLogo} className="h-[24px]" />
                            </div>
                        </div>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} size="xs" closeOnEscape onClose={closeDialog}>
            <Dialog.Content aria-label="rackbeatlager">
                <Dialog.Title>
                    <Trans>Uninstall Inventory by Rackbeat </Trans>
                </Dialog.Title>
                <div className="text-center mt-4 mb-6">
                    <img src={RackbeatOffboardGraphic} className="mx-auto w-80" />
                </div>
                <div className="text-left mt-4 mb-6">
                    <Trans>
                        We are sorry that you want to remove your Rackbeat app. Please note that the action is binding when you
                        click on the 'complete' button.
                    </Trans>
                </div>
                <div className="text-left mt-4 mb-6">
                    <Trans>You will no longer have access to Rackbeat going forward.</Trans>
                </div>
                <div className="flex justify-center">
                    <div className="w-full">
                        <Button appearance="primary" onClick={handleOffboardingCompleted} className="w-full !h-14 text-[14px]">
                            <Trans>Complete</Trans>
                        </Button>
                    </div>
                </div>
                <Dialog.Footer className="flex justify-between bg-grey-100 -mx-6 -mb-6 align-middle pt-2 pb-4">
                    <div className="flex text-end ml-6 pt-2">
                        <div className="text-xs mr-4 mt-1">
                            <Trans>In close collaboration with</Trans>
                        </div>
                        <div>
                            <img src={RackbeatLogo} className="h-[24px]" />
                        </div>
                    </div>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
}

export { RackbeatLagerOffboardingDialog as RackbeatLagerOffboardingDialogNative };
